<template>
    <div
            v-if="parseInt(landingPageTypeId) === LU_LP_TYPES.HOME_OWNERSHIP && this.landingPage.uuid"
            class="landing-page-layout tw-w-full tw-h-auto tw-flex tw-flex-col tw-bg-white tw-justify-between tw-items-center tw-overflow-hidden"
    >
        <landing-page-hero
                :landing-page="landingPage"
                :project-type="HOHProjectTypeUuid"
        />
        <landing-page-how-it-works
                :landing-page="landingPage"
        />
        <landing-page-testimonial
                :base-color="landingPage.primaryColor"
        />
        <landing-page-ready-to-get-started
                :landing-page="landingPage"
                :project-type="HOHProjectTypeUuid"
        />
        <landing-page-real-estate-agent
                v-if="landingPage.exists"
                :landing-page="landingPage"
        />
        <interstitial-modal
                :loading="interstitialModalRedirecting"
        />
        <popover-card
                v-if="landingPage.exists && $route.name !== 'landing-page.home'"
                :landing-page="landingPage"
                :target="AVATAR_IMG_ID"
        />
        <claim-your-home-modal :landing-page-primary-color="landingPage.primaryColor" />
    </div>
    <div v-else>
        <div
                :class="{ 'background-grey': $route.name !== 'landing-page.trade' }"
                class="landing-page-layout"
        >
            <template v-if="landingPage.exists && !isLoading">
                <landing-page-header
                        :action-btn-text="landingPage.home.actionButtonText"
                        :avatar="avatar"
                        :btn-action-callback="btnActionCallback"
                        :class="headerCss"
                        :landing-page="landingPage"
                        :links="filteredLinks"
                />
                <common-sidebar
                        :nav-item-active="activeItem"
                        :nav-items="filteredLinks"
                        :nav-items-bottom="[]"
                        backdrop
                        no-sign-out
                        right
                        @click="handleNavItemClick"
                >
                    <template #logo>
                        <agent-card
                                :avatar="landingPageCardDetails.avatar"
                                :avatar-size="6.25"
                                :company-logo="landingPageCardDetails.companyLogo"
                                :display-name="landingPageCardDetails.displayName"
                                :email="landingPageCardDetails.email"
                                :phones="landingPageCardDetails.phones"
                                :social-links="landingPageCardDetails.socialLinks"
                                occupation="Real Estate Agent"
                        />
                    </template>
                    <template #bottom>
                        <div class="btn-get-started-wrapper p-3 w-100 mt-auto">
                            <b-button
                                    :href="redirectLink"
                                    block
                                    class="btn-get-started"
                                    size="lg"
                                    variant="primary"
                            >
                                {{ landingPage.home.actionButtonText }}
                            </b-button>
                        </div>
                    </template>
                </common-sidebar>
                <div
                        class="page-content full-width-container"
                >
                    <router-view
                            :has-service-providers="hasServiceProviders"
                            :is-mortgage-available="isMortgageAvailable"
                            :landing-page="landingPage"
                            :mortgage-resource="mortgageResource"
                            :service-provider-list="serviceProvidersList"
                    />
                </div>
                <landing-page-footer-hoh
                        v-if="showFooter && pageIsHomeOwnership"
                        :landing-page="landingPage"
                />
                <landing-page-footer
                        v-else-if="showFooter"
                        :links="landingPage.links.toArray()"
                        :notice="landingPage.notice"
                        :organization-privacy-policy-url="organization.privacyPolicyUrl || null"
                />
            </template>

            <template v-else>
                <header-loading :has-avatar="hasAvatar" />
                <component :is="pageLoadingTemplate" />
            </template>
            <interstitial-modal :loading="interstitialModalRedirecting" />
            <popover-card
                    v-if="landingPage.exists && $route.name !== 'landing-page.home'"
                    :landing-page="landingPage"
                    :target="AVATAR_IMG_ID"
            />
            <claim-your-home-modal :landing-page-primary-color="landingPage.primaryColor" />
        </div>
    </div>
</template>

<script>
import './LandingPageLayout.scss';
import './LandingPageLayout.sm.scss';
import LandingPageAppResource, {
    SECTIONS
} from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageAppResource";
import {
    ALL as LU_LP_TYPES,
    LIFE_EVENTS,
    TOPIC_OF_INTERESTS
} from "@milestoneslabs/milestones-core-js/src/AppResources/LuLandingPageTypeAppResource";
import {AVATAR_IMG_ID} from "../../components/LandingPage/LandingPageHeader/LandingPageHeader";
import {myProjectsUrl, newProjectUrl, redirectTo} from "../../components/helpers/externalRoutes";
import {
    DESTINED_TO as SOCIAL_LINK_DESTINED_TO,
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    SOCIAL_LINKS,
    WEBSITE,
    YOUTUBE,
} from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageSocialLinkAppResource";
import {
    BUY,
    FINANCE,
    HOME,
    LEARN,
    SELL,
    TRADE,
} from "@milestoneslabs/milestones-core-js/src/AppResources/LuLandingPageSectionTypeAppResource";
import find from 'lodash/find';
import InterstitialModal, {
    INTERSTITIAL_MODAL_REDIRECT
} from "../../components/LandingPage/Modals/InterstitiallModal/InterstitialModal";
import HomeLoading from "../../components/LandingPage/LoadingScreens/HomeLoading/HomeLoading";
import FileAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/FileAppResource";
import AgentAndLoanOfficerHomeLoading
    from "../../components/LandingPage/LoadingScreens/AgentAndLoanOfficerHomeLoading/AgentAndLoanOfficerHomeLoading";
import BabyOrAdoptionHomeLoading
    from "../../components/LandingPage/LoadingScreens/BabyOrAdoptionHomeLoading/BabyOrAdoptionHomeLoading";
import {
    DESTINED_TO as PHONE_DESTINED_TO
} from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPagePhoneAppResource";
import LandingPageViewAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageViewAppResource";
import TopicOfInterestHomeLoading
    from "../../components/LandingPage/LoadingScreens/TopicOfInterestHomeLoading/TopicOfInterestHomeLoading";
import AppResourceSearch from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceSearch";
import ApiFilter from "@milestoneslabs/milestones-js-engine/src/Api/ApiFilter";
import Resource7Mixin from "../../Mixins/Resource7Mixin";
import LandingPageFinanceSectionAppResource
    from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageFinanceSectionAppResource";
import ClaimYourHomeModal from "../../components/LandingPage/Modals/ClaimYourHomeModal/ClaimYourHomeModal";
import {mapActions, mapGetters, mapState} from "vuex";
import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
import {HOME_OWNERSHIP} from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceTypeAppResource";
import PageBrandingDataMixin from "../../Mixins/PageBrandingDataMixin";
import {privacyPolicy, termsOfUse} from "@milestoneslabs/milestones-ui-kit/src/helpers/externalLinks";
import LandingPageHero from "../../components/LandingPage/LandingPageV2/LandingPageHero.vue";
import LandingPageHowItWorks from "../../components/LandingPage/LandingPageV2/LandingPageHowItWorks.vue";
import LandingPageTestimonial from "../../components/LandingPage/LandingPageV2/LandingPageTestimonial.vue";
import LandingPageReadyToGetStarted from "../../components/LandingPage/LandingPageV2/LandingPageReadyToGetStarted.vue";
import LandingPageRealEstateAgent from "../../components/LandingPage/LandingPageV2/LandingPageRealEstateAgent.vue";
import PopoverCard from "../../components/LandingPage/PopoverCard/PopoverCard.vue";
import isMobileMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IsMobileMixin";
import AgentCard from "../../components/LandingPage/AgentCard/AgentCard.vue";
import LandingPageHeader from "../../components/LandingPage/LandingPageHeader/LandingPageHeader.vue";
import LandingPageFooterHoh
    from "../../components/LandingPage/LandingPageFooter/LandingPageFooterHoh/LandingPageFooterHoh.vue";
import LandingPageFooter from "../../components/LandingPage/LandingPageFooter/LandingPageFooter.vue";
import HeaderLoading from "../../components/LandingPage/LoadingScreens/HeaderLoading/HeaderLoading.vue";

export const STANDARD_LINKS = [
    {
        icon: 'fa-home',
        label: 'Home',
        to: {name: 'landing-page.home'},
        slug: SECTIONS.HOME,
        action: HOME,
    },
    {
        icon: 'fa-search',
        label: 'Buy',
        to: {name: 'landing-page.buy'},
        slug: SECTIONS.BUY,
        action: BUY,
    },
    {
        icon: 'fa-flag',
        label: 'Sell',
        to: {name: 'landing-page.sell'},
        slug: SECTIONS.SELL,
        action: SELL,
    },
    {
        icon: 'fa-sync-alt',
        label: 'Trade-In',
        to: {name: 'landing-page.trade'},
        slug: SECTIONS.TRADE,
        action: TRADE,
    },
    {
        icon: 'fa-info-circle',
        label: 'Learn',
        to: {name: 'landing-page.learn'},
        slug: SECTIONS.LEARN,
        action: LEARN,
    },
];

export const HOH_LINKS = [];

export default {
    name: "LandingPageLayout",
    components: {
        HeaderLoading,
        LandingPageFooter,
        LandingPageFooterHoh,
        LandingPageHeader,
        AgentCard,
        PopoverCard,
        InterstitialModal,
        ClaimYourHomeModal,
        LandingPageRealEstateAgent,
        LandingPageReadyToGetStarted,
        LandingPageTestimonial,
        LandingPageHowItWorks,
        LandingPageHero,
    },

    mixins: [
        Resource7Mixin,
        ShowNotificationMixin,
        PageBrandingDataMixin,
        isMobileMixin,
    ],

    data() {
        return {
            AVATAR_IMG_ID,
            LU_LP_TYPES,
            organizationBranding: window.organizationBranding,
            landingPageUuid: window.landingPageUuid,
            landingPageTypeId: window.landingPageTypeId,
            landingPage: new LandingPageAppResource({}),
            interstitialModalRedirecting: false,
            links: parseInt(window.landingPageTypeId) === LU_LP_TYPES.HOME_OWNERSHIP ? HOH_LINKS : STANDARD_LINKS,
            filteredLinks: [],
            isLoading: false,
            financeAppResource: new LandingPageFinanceSectionAppResource({}),
            HOHProjectTypeUuid: '',
            termsOfUse,
        };
    },
    computed: {
        SOCIAL_LINKS() {
            return SOCIAL_LINKS;
        },
        ...mapGetters([
            'getCurrentProjectMlsId',
            'getDefaultMapCoordinates',
        ]),
        ...mapState({
            authUser: state => state.landingPage.authUser
        }),
        privacyPolicyUrl() {
            if (window.organizationPrivacyPolicyUrl) {
                return window.organizationPrivacyPolicyUrl;
            }

            return privacyPolicy;
        },
        internalSocialLinks() {
            let socialLinks = [];
            this.socialLinks.forEach(item => {
                let order = 0;
                switch (item.type) {
                    case SOCIAL_LINKS.WEBSITE:
                        order = 1;
                        break;
                    case SOCIAL_LINKS.FACEBOOK:
                        order = 2;
                        break;
                    case SOCIAL_LINKS.LINKEDIN:
                        order = 3;
                        break;
                    case SOCIAL_LINKS.YOUTUBE:
                        order = 4;
                        break;
                    case SOCIAL_LINKS.INSTAGRAM:
                        order = 5;
                        break;
                    case SOCIAL_LINKS.WHATSAPP:
                        order = 6;
                        break;
                    case SOCIAL_LINKS.SKYPE:
                        order = 7;
                        break;
                    default:
                        order = 0;
                }
                socialLinks.push({
                    type: item.type,
                    url: item.url,
                    order: order,
                });
            });
            socialLinks.sort((item, compareItem) => {
                return item.order - compareItem.order;
            });
            return socialLinks;
        },
        companyLogo() {
            return (this.landingPage.organizationLogo || {}).publicStorageUrl || null;
        },
        redirectLink() {
            if (!this.landingPage.exists) {
                return '';
            }
            const queryParams = {
                landingPageUuid: landingPageUuid,
                brandedLogin: window.brandedLogin,
            };
            return newProjectUrl(queryParams);
        },
        activeItem() {
            let item = {};
            switch (this.$route.name) {
                case 'landing-page.home':
                    item = find(this.filteredLinks, {action: HOME});
                    break;
                case 'property.search':
                case 'property.details.page':
                    item = find(this.filteredLinks, {action: BUY});
                    break;
                case 'landing-page.sell':
                    item = find(this.filteredLinks, {action: SELL});
                    break;
                case 'landing-page.finance':
                    item = {};
                    break;
                case 'landing-page.trade':
                    item = find(this.filteredLinks, {action: TRADE});
                    break;
                case 'landing-page.learn':
                    item = find(this.filteredLinks, {action: LEARN});
                    break;
            }
            return item;
        },
        avatar() {
            let src = this.landingPage.avatar ? this.landingPage.avatar : new FileAppResource();
            if (this.$route.name === 'landing-page.home') {
                src = new FileAppResource();
            }
            return src;

        },
        showFooter() {
            let show = true;
            if ([
                'landing-page.sell.get-instant-cash',
                'property.search',
                'property.details.page',
                'landing-page.hub-preview'
            ].includes(this.$route.name)) {
                show = false;
            }
            return show;
        },
        name() {
            let name = this.landingPage.firstName + ' ' + this.landingPage.lastName;
            if (this.landingPage.displayName) {
                name = this.landingPage.displayName;
            }
            return name;
        },
        website() {
            let website = find(this.landingPage.socialLinks.toArray(), {type: WEBSITE});
            if (website) {
                return website.url;
            }
            return '';
        },
        socialLinks() {
            let socialLinks = [];
            this.landingPage.socialLinks.toArray().forEach(socialLink => {
                let icon;

                if (socialLink.type === WEBSITE) {
                    return;
                }
                switch (socialLink.type) {
                    case FACEBOOK:
                        icon = 'fa-facebook-f';
                        break;
                    case LINKEDIN:
                        icon = 'fa-linkedin-in';
                        break;
                    case YOUTUBE:
                        icon = 'fa-youtube';
                        break;
                    case INSTAGRAM:
                        icon = 'fa-instagram';
                        break;
                    default:
                        icon = 'fa-link';
                }

                socialLinks.push({
                    icon: icon,
                    url: socialLink.url,
                });
            });

            return socialLinks;
        },
        pageLoadingTemplate() {
            switch (this.$route.name) {
                case 'landing-page.home':
                    if (parseInt(this.landingPageTypeId) === LU_LP_TYPES.REAL_ESTATE) {
                        return HomeLoading;
                    }
                    if (parseInt(this.landingPageTypeId) === LU_LP_TYPES.AGENT_AND_LOAN_OFFICER) {
                        return AgentAndLoanOfficerHomeLoading;
                    }

                    if (LIFE_EVENTS.includes(parseInt(this.landingPageTypeId))) {
                        return BabyOrAdoptionHomeLoading;
                    }

                    if (TOPIC_OF_INTERESTS.includes(parseInt(this.landingPageTypeId))) {
                        return TopicOfInterestHomeLoading;
                    }
                    break;
                default:
                    return undefined;
            }
        },
        hasAvatar() {
            return this.$route.name !== 'landing-page.home' && this.landingPage.avatar;
        },
        headerCss() {
            let css = [];
            if (this.$route.name === 'property.details.page') {
                css.push('d-none d-md-block');
            }
            return css;
        },
        landingPageCardDetails() {
            let details = {
                avatar: this.landingPage.avatar,
                displayName: this.landingPage.firstName + ' ' + this.landingPage.lastName,
                email: this.landingPage.email,
                companyLogo: (this.landingPage.organizationLogo || {}).publicStorageUrl || null,
                phones: [],
                socialLinks: [],
                notice: this.landingPage.notice,
                links: this.landingPage.links,
            };
            if (this.landingPage.phones) {
                details.phones = this.landingPage.phones.toArray().filter(item => {
                    return item.destinedTo === PHONE_DESTINED_TO.REAL_ESTATE_AGENT;
                });
            }
            if (this.landingPage.socialLinks) {
                details.socialLinks = this.landingPage.socialLinks.toArray().filter(item => {
                    return item.destinedTo === SOCIAL_LINK_DESTINED_TO.REAL_ESTATE_AGENT;
                });
            }
            return details;
        },
        pageIsHomeOwnership() {
            return parseInt(landingPageTypeId) === LU_LP_TYPES.HOME_OWNERSHIP;
        },
    },
    methods: {
        ...mapActions({
            setAuthUser: 'setAuthUser',
        }),
        async fetchHomeOwnershipProjectType() {
            let projectTypeSearch = new AppResourceSearch('workspace-type');

            await projectTypeSearch.setSearchData({
                limit: 1,
                filter: new ApiFilter({
                    params: {
                        type: HOME_OWNERSHIP,
                    },
                }),
            }, false);

            try {
                await projectTypeSearch.search();

                if (projectTypeSearch.appResourceCollection.isNotEmpty) {
                    let item = projectTypeSearch.appResourceCollection.getFirst();
                    this.HOHProjectTypeUuid = item.uuid;
                }
            } catch (e) {
                console.warn(e);
            }
        },
        async fetchLandingPage() {
            if (!this.landingPageUuid) {
                return;
            }

            try {
                const landingPageSearch = new AppResourceSearch('landing-page');
                await landingPageSearch.setSearchData({
                    limit: 1,
                    include: [
                        'home.cover',
                        'home.loanOfficer.avatar',
                        'home.loanOfficer.organizationLogo',
                        'home.topicOfInterest',
                        'avatar',
                        'phones',
                        'socialLinks',
                        'links',
                        'organizationLogo',
                        'actions',
                        'landingPageType',
                        'organization.organizationBranding.favicon',
                        'organization.organizationBranding.logo',
                        'event',
                        'buy.playbook',
                        'sell.playbook',
                        'sell.projectMembershipType',
                        'user.loanOfficerLicense',
                        'user.organizationLogo',
                        'user.avatarFile',
                    ],
                    filter: new ApiFilter({
                        params: {
                            uuid: this.landingPageUuid,
                        }
                    }),
                }, false);

                await landingPageSearch.search();
                this.landingPage = landingPageSearch.appResourceCollection.getFirst();
                this.organization = this.landingPage.organization;
                this.syncInformationWithMyOrganization();
            } catch (e) {
                console.warn(e);
            }
        },

        syncInformationWithMyOrganization() {
            if (this.landingPage.syncOrganization && this.organization?.organizationBranding?.logo) {
                this.landingPage.organizationLogo = this.organization.organizationBranding.logo;
            }
        },

        async fetchFinanceSection() {
            if (
                !this.landingPage.exists ||
                !this.landingPage.activePages.includes(SECTIONS.FINANCE)
            ) {
                return;
            }

            try {
                let search = new AppResourceSearch('landing-page-finance-section');
                await search.setSearchData({
                    filter: new ApiFilter({
                        params: {
                            landingPageUuid: this.landingPage.uuid
                        }
                    })
                }, false);

                await search.search();

                if (search.appResourceCollection.isNotEmpty) {
                    this.financeAppResource = search.appResourceCollection.getFirst();
                }
            } catch (e) {
                console.error(e);
                this.showNotification({
                    message: `Something went wrong while fetching finance section. Please, try again later.`,
                    type: 'error',
                });
            }
        },

        setFilteredLinks() {
            this.filteredLinks = this.links.filter((link) => {
                if (
                    this.landingPage.hasOwnProperty('activePages') &&
                    this.landingPage.activePages.includes(link.slug)
                ) {
                    if (
                        link.slug === SECTIONS.FINANCE &&
                        this.financeAppResource.hasMultipleLenders &&
                        (
                            !this.hasServiceProvidersList ||
                            !this.isMortgageAvailable ||
                            !this.hasServiceProviders
                        )
                    ) {
                        return false;
                    }

                    return true;
                }

                return link.slug === SECTIONS.HOME;
            });
        },

        handleNavItemClick(value) {
            switch (value.action) {
                case HOME:
                    this.$router.push({name: 'landing-page.home'});
                    break;
                case BUY:
                    this.$router.push({name: 'landing-page.buy'});
                    break;
                case SELL:
                    this.$router.push({name: 'landing-page.sell'});
                    break;
                case FINANCE:
                    this.$router.push({name: 'landing-page.finance'});
                    break;
                case TRADE:
                    this.$router.push({name: 'landing-page.trade'});
                    break;
                case LEARN:
                    this.$router.push({name: 'landing-page.learn'});
                    break;
                default:
                    this.$router.push({name: 'index'});

            }
        },

        btnActionCallback() {
            if (Boolean(this.authUser?.uuid) && this.landingPage.userUuid === this.authUser.uuid) {
                this.showNotification({
                    message: 'You can not create a hub from your own Landing Page! Please log in as another user!',
                    type: 'error',
                });
                return;
            }

            return redirectTo(myProjectsUrl({
                landingPageUuid: landingPageUuid,
                brandedLogin: window.brandedLogin,
                type: this.HOHProjectTypeUuid,
            }));
        },

        createView() {
            if (this.getCookie(this.landingPage.uuid)) {
                return;
            }

            let landingPageViewAppResource = new LandingPageViewAppResource({
                attributes: {
                    landingPageUuid: this.landingPage.uuid,
                },
            });

            try {
                landingPageViewAppResource.create();
                this.setCookie(this.landingPage.uuid, this.landingPage.uuid, 5);
            } catch (exception) {
                console.error(exception);
            }
        },

        async handleInterstitialModalRedirect(redirectLink) {
            this.interstitialModalRedirecting = true;

            try {
                await this.landingPage.action();
            } catch (e) {
                console.error(e);
            }

            window.location.href = redirectLink;
        },

        setCookie(name, value, minutes = null) {
            let expires = "";

            if (minutes) {
                let date = new Date();
                date.setTime(date.getTime() + (minutes * 60 * 1000));
                expires = "; expires=" + date.toUTCString();
            }

            document.cookie = name + "=" + (value || "") + expires;
        },

        getCookie(name) {
            let cookie = document.cookie
                .split('; ')
                .find((cookie) => {
                    return cookie.startsWith(name + '=');
                });

            let cookieValue = null;
            if (cookie) {
                cookieValue = cookie.split('=')[1];
            }

            return cookieValue;
        },

    },

    async mounted() {
        if (!window.landingPageExists) {
            this.$router.push({path: '*'});
            return;
        }

        try {
            this.isLoading = true;
            await this.setAuthUser();

            await this.fetchLandingPage();
            await this.fetchFinanceSection();
            await this.fetchHomeOwnershipProjectType();

            this.setPageTitle();
            this.setOrganizationFavicon();

            this.setAvailablePlaybooks();
            this.setAvailableServiceProviderList();

            await this.fetchMortgageResource();
            // await this.fetchServiceProviders();

            this.createView();
            this.setFilteredLinks();

        } catch (e) {
            console.error(e);
        } finally {
            this.isLoading = false;
        }

        this.$bus.$on(INTERSTITIAL_MODAL_REDIRECT, this.handleInterstitialModalRedirect);
    },

    beforeDestroy() {
        this.$bus.$off(INTERSTITIAL_MODAL_REDIRECT, this.handleInterstitialModalRedirect);
    },

};
</script>
